/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { axios, publicAxios, baseUrl, endpoints } from 'Api';
import { AppContext, AuthContext } from 'Context';
import FinishSetupModal from 'CreateNewUser/v2/FinishSetupModal';
import styles from './index.module.css';

const rowName = (u) => `${u?.firstname || ''} ${u?.lastname || ''}`.trim()
  || u?.email?.split('@')[0] || '—';
const planLabel = (u) => {
  const base = u?.subscription?.product_name || u?.plan_name || 'Free';
  return u?.expired === true ? `${base} (Expired)` : base;
};
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
// "10% off" / "$5.00 off" for a promo the user purchased with (BE `promo` object).
// Amounts from the BE are already in dollars; percentage `value` is a percent.
const promoOffLabel = (p) => (p?.type === 'percentage'
  ? `${p.value}% off`
  : `${money(p?.discount_amount)} off`);
const lastAccessedLabel = (u) => {
  if (!u?.last_accessed) return '—';
  const d = new Date(u.last_accessed);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

// V2-structured user-management panel (placement mirrors /manage-users V2), but
// styled with this page's CSS modules instead of MUI. Skeleton scope: list +
// tabs + search + Create + Access. Secondary row actions (invite / copy link /
// hide) are intentionally deferred.
const UsersPanel = ({
  blockCreateUser, onCreateUserBlocked, canViewViewers = false,
  // Embedded mode (inside the plan-users modal): hide the section header
  // (title / search / export / create) and restrict rows to those matching
  // `rowFilter`. All row actions + sub-modals stay identical.
  hideHeader = false, rowFilter = null, emptyLabel = null,
}) => {
  const navigate = useNavigate();
  const { getToken } = useContext(AuthContext);
  const {
    setAppSnackbar, switchingFunction, isTokenChange,
  } = useContext(AppContext);

  const [managerUsers, setManagerUsers] = useState([]);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Only the "Users" list now (the affiliate code + tab header moved to the page
  // header); kept as a constant so the existing filters/labels still read.
  const tab = 'users';
  const [search, setSearch] = useState('');
  const [accessTarget, setAccessTarget] = useState(null);
  const [accessing, setAccessing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Per-row action in flight (send invite SMS / copy signup link).
  const [invitingId, setInvitingId] = useState(null);
  const [copyingUserId, setCopyingUserId] = useState(null);
  // "Finish Setup" target — opens the plan-picker → payment popup for this user.
  const [finishUser, setFinishUser] = useState(null);
  // "View viewers" popup — lists the selected user's active + pending viewers.
  const [viewersTarget, setViewersTarget] = useState(null);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [viewersActive, setViewersActive] = useState([]);
  const [viewersPending, setViewersPending] = useState([]);

  // `silent` refetches in the background: the table keeps showing the current
  // rows instead of collapsing into the "Loading users…" state.
  const loadUsers = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const token = await getToken();
      const [mgr, inv] = await Promise.all([
        axios.post(`${baseUrl}/managerUsersList`, { token }).catch(() => null),
        axios.post(`${baseUrl}/inviteUsersList`, { token }).catch(() => null),
      ]);
      setManagerUsers(Array.isArray(mgr?.data?.data) ? mgr.data.data : []);
      const invData = inv?.data;
      const invList = Array.isArray(invData) ? invData : (invData?.data || invData?.users || []);
      setConnectedUsers(Array.isArray(invList) ? invList : []);
    } catch (e) {
      // A failed background refresh leaves the rows on screen; only the
      // foreground load clears them.
      if (!silent) {
        setManagerUsers([]);
        setConnectedUsers([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { loadUsers(); }, [loadUsers, isTokenChange]);

  const baseRowsAll = tab === 'connected' ? [...connectedUsers].reverse() : [...managerUsers].reverse();
  // Embedded mode filters the list to one plan tile's users (same cycle/bucket
  // rule the modal passes in); full page shows everyone.
  const baseRows = typeof rowFilter === 'function' ? baseRowsAll.filter(rowFilter) : baseRowsAll;
  const q = search.trim().toLowerCase();
  const shown = baseRows.filter((u) => {
    if (!q) return true;
    const name = `${u?.firstname || ''} ${u?.lastname || ''}`.toLowerCase();
    return name.includes(q) || (u?.email || '').toLowerCase().includes(q);
  });

  // Export the current tab's (search-filtered) users to CSV — same format as
  // the /manage-users list (ManagerList.exportUsersToCSV).
  const exportUsersToCSV = () => {
    if (!shown.length) {
      setAppSnackbar?.({ isVisible: true, type: 'error', message: 'No users to export' });
      return;
    }
    const fmtDate = (v) => {
      if (!v) return '';
      const d = new Date(v);
      return Number.isNaN(d.getTime())
        ? ''
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };
    const esc = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const headers = ['Name', 'Email', 'Plan', 'Promo Code', 'Promo Discount', 'Amount Paid', 'End Date', 'Last Accessed'];
    const rows = [headers.join(',')];
    shown.forEach((u) => {
      const name = `${u?.firstname || ''} ${u?.lastname || ''}`.trim();
      const promoDiscount = u?.promo
        ? `${promoOffLabel(u.promo)} (saved ${money(u.promo.discount_amount)})`
        : '-';
      rows.push([
        esc(name),
        esc(u?.email || ''),
        esc(planLabel(u)),
        esc(u?.promo?.code || '-'),
        esc(promoDiscount),
        esc(u?.promo ? money(u.promo.final_amount) : '-'),
        esc(fmtDate(u?.subscription?.end_date) || '-'),
        esc(fmtDate(u?.last_accessed)),
      ].join(','));
    });
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const base = tab === 'connected' ? 'connected-accounts' : 'users';
    link.download = `${base}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setAppSnackbar?.({ isVisible: true, type: 'success', message: 'User list exported successfully' });
  };

  // Create → menu with two flows: Quick user and User with a plan. When
  // create-user is gated (affiliate but membership not active), route to plans.
  const onCreateClick = () => {
    if (blockCreateUser) { onCreateUserBlocked?.(); return; }
    setMenuOpen((v) => !v);
  };
  // Accessing a user's account is gated the same way as create: no active
  // membership → show the activate-membership popup instead of switching in.
  const onAccessClick = (u) => {
    if (blockCreateUser) { onCreateUserBlocked?.(); return; }
    setAccessTarget(u);
  };
  const createUser = (userType) => {
    setMenuOpen(false);
    navigate(`/create-user-v2?type=${userType}`);
  };

  // "Finish Setup" — for affiliates who abandoned after step 1 (user created,
  // still on a free plan). Resume step 2 in a popup (like the creation flow):
  // pick a plan → pay, all in a modal, no full-page redirect.
  const finishSetup = (u) => setFinishUser(u);

  // Send the invite SMS to a created user (only before they've signed up).
  // Optimistically flips the row to "invited" (signup_status '2'), reverting on
  // failure.
  const sendInvite = async (u) => {
    if (!u?.userId) return;
    setInvitingId(u.userId);
    const setStatus = (status) => setManagerUsers((prev) => prev.map((row) => (
      row.userId === u.userId ? { ...row, signup_status: status } : row)));
    setStatus('2');
    try {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}/sendInviteLink`, { token, userId: u.userId });
      if (data?.success) {
        setAppSnackbar?.({ isVisible: true, type: 'success', message: data?.message || 'Invitation sent successfully' });
        loadUsers(true);
      } else {
        setStatus('0');
        setAppSnackbar?.({ isVisible: true, type: 'error', message: data?.message || 'Failed to send invitation' });
      }
    } catch (e) {
      setStatus('0');
      setAppSnackbar?.({ isVisible: true, type: 'error', message: e?.response?.data?.message || 'Failed to send invitation' });
    } finally {
      setInvitingId(null);
    }
  };

  // Copy the created user's signup link. Safari only allows clipboard.write()
  // synchronously inside the gesture, so the fetch is handed to ClipboardItem as
  // a promise; other browsers fall back to writeText() after the await.
  const copySignupLink = (u) => {
    if (!u?.userId) return;
    setCopyingUserId(u.userId);
    const fetchLink = (async () => {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}${endpoints.copySignupLink}`, { token, userId: u.userId });
      if (data?.success && data?.data?.short_url) return data.data.short_url;
      throw new Error(data?.message || 'Failed to get signup link');
    })();
    const ok = () => setAppSnackbar?.({ isVisible: true, type: 'success', message: 'Signup link copied to clipboard' });
    const fail = (e) => setAppSnackbar?.({ isVisible: true, type: 'error', message: e?.message || 'Failed to copy signup link' });
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      const item = new ClipboardItem({
        'text/plain': fetchLink.then((url) => new Blob([url], { type: 'text/plain' })),
      });
      navigator.clipboard.write([item]).then(ok).catch(fail).finally(() => setCopyingUserId(null));
    } else {
      fetchLink
        .then((url) => navigator.clipboard.writeText(url))
        .then(ok)
        .catch(fail)
        .finally(() => setCopyingUserId(null));
    }
  };

  const confirmAccess = async () => {
    if (!accessTarget) return;
    setAccessing(true);
    try {
      await switchingFunction?.(accessTarget);
    } catch (e) { /* switchingFunction surfaces its own errors */ } finally {
      setAccessing(false);
      setAccessTarget(null);
    }
  };

  // Open the "View viewers" popup for a user and load their active + pending
  // viewers. The viewer endpoints authenticate the token in the POST body, so we
  // pass the user's OWN token via publicAxios (the normal `axios` interceptor
  // would overwrite it with the manager's token) — no account switch needed.
  const openViewers = async (u) => {
    if (!u?.token) return;
    setViewersTarget(u);
    setViewersLoading(true);
    setViewersActive([]);
    setViewersPending([]);
    try {
      const [act, pen] = await Promise.all([
        publicAxios.post(`${baseUrl}/${endpoints.getViewersList}`, { token: u.token }).catch(() => null),
        publicAxios.post(`${baseUrl}/${endpoints.getPendingViewersList}`, { token: u.token }).catch(() => null),
      ]);
      setViewersActive(Array.isArray(act?.data?.data) ? act.data.data : []);
      setViewersPending(Array.isArray(pen?.data?.data) ? pen.data.data : []);
    } catch (e) {
      setAppSnackbar?.({ isVisible: true, type: 'error', message: 'Failed to load viewers' });
    } finally {
      setViewersLoading(false);
    }
  };

  // Export the open popup's viewers (active + pending) to CSV.
  const exportViewersToCSV = () => {
    const all = [
      ...viewersActive.map((v) => ({ v, status: 'Active' })),
      ...viewersPending.map((v) => ({ v, status: 'Pending' })),
    ];
    if (!all.length) {
      setAppSnackbar?.({ isVisible: true, type: 'error', message: 'No viewers to export' });
      return;
    }
    const esc = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const rows = [['Name', 'Email', 'Status'].join(',')];
    all.forEach(({ v, status }) => {
      const name = `${v?.firstname || ''} ${v?.lastname || ''}`.trim() || (v?.email?.split('@')[0] || '');
      rows.push([esc(name), esc(v?.email || ''), esc(status)].join(','));
    });
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // Name the file after the account owner whose viewers these are.
    const t = viewersTarget || {};
    const who = `${t.firstname || ''} ${t.lastname || ''}`.trim()
      || (t.email ? t.email.split('@')[0] : '')
      || 'viewers';
    link.download = `${who}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setAppSnackbar?.({ isVisible: true, type: 'success', message: 'Viewers exported successfully' });
  };

  // One viewer row in the popup — avatar, name/email, and an Active/Pending label.
  const renderViewerRow = (v, kind, i) => {
    const vName = `${v?.firstname || ''} ${v?.lastname || ''}`.trim()
      || v?.email?.split('@')[0] || '—';
    const isActive = kind === 'active';
    return (
      <div
        key={`${kind}-${v?.id || v?.email || i}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 10px',
          border: '1px solid #eef0f4',
          borderRadius: 10,
        }}
      >
        <span className={styles.uAvatar}>
          {v?.image ? <img src={v.image} alt={vName} /> : vName.charAt(0).toUpperCase()}
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          <span style={{
            fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
          >
            {vName}
          </span>
          <span style={{
            fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
          >
            {v?.email}
          </span>
        </span>
        <span style={{
          flexShrink: 0,
          padding: '2px 10px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          background: isActive ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.14)',
          color: isActive ? '#059669' : '#b45309',
        }}
        >
          {isActive ? 'Active' : 'Pending'}
        </span>
      </div>
    );
  };

  return (
    <div className={styles.usersPanel}>
      {/* ---- Section header: title (left) + search / export / create (right) ---- */}
      {!hideHeader && (
      <div className={styles.usersHead}>
        <h2 className={styles.usersTitle}>Manage Users</h2>
        <div className={styles.usersHeadActions}>
          <input
            className={styles.usersSearch}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users"
          />
          <button
            type="button"
            className={styles.exportBtn}
            onClick={exportUsersToCSV}
            disabled={!shown.length}
            title="Export the current list as CSV"
          >
            <span className={styles.exportIcon} aria-hidden="true">↓</span>
            Export
          </button>
          <div className={styles.createWrap}>
            <button type="button" className={styles.createBtn} onClick={onCreateClick}>
              <span className={styles.createPlus}>+</span>
              Create
            </button>
            {menuOpen && (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 19 }}
                  onClick={() => setMenuOpen(false)}
                />
                <div className={styles.createMenu} role="menu">
                  <button type="button" className={styles.createMenuItem} onClick={() => createUser('quick')}>
                    Quick Set Up
                  </button>
                  <button type="button" className={styles.createMenuItem} onClick={() => createUser('withPlan')}>
                    Full Service Set Up
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      )}

      {/* ---- Table ---- */}
      {loading ? (
        <p className={styles.usersState}>Loading users…</p>
      ) : !shown.length ? (
        <p className={styles.usersState}>
          {/* eslint-disable-next-line no-nested-ternary */}
          {q
            ? 'No users match your search.'
            : (emptyLabel || `No ${tab === 'connected' ? 'connected accounts' : 'users'} yet.`)}
        </p>
      ) : (
        <div className={styles.uTableWrap}>
          <table className={styles.uTable}>
            <thead>
              <tr>
                <th className={styles.uTh}>Name</th>
                <th className={styles.uTh}>Plan</th>
                <th className={styles.uTh}>Last accessed</th>
                <th className={`${styles.uTh} ${styles.uThRight}`}>Action</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((u, idx) => {
                const name = rowName(u);
                const expired = u?.expired === true;
                // Affiliate abandoned after step 1: user is still on a free plan.
                // "Free" comes through either as no subscription at all or as a
                // plan explicitly named "Free" (subscription.plan_name /
                // product_name). Offer "Finish Setup" to resume the plan
                // purchase, and hide invite/copy/access for them.
                const planName = (
                  u?.subscription?.product_name
                  || u?.subscription?.plan_name
                  || u?.plan_name
                  || ''
                ).toString().trim().toLowerCase();
                const planFree = planName === '' || planName === 'free';
                const needsFinish = tab === 'users' && Boolean(u?.userId) && planFree;
                const canAccess = !needsFinish && Boolean(u?.userId && u?.token);
                // Invite SMS + copy signup link only make sense for a user I
                // created who hasn't signed up yet ('0' = not invited, '2' = invited).
                const canInvite = !needsFinish && tab === 'users' && Boolean(u?.userId)
                  && (u?.signup_status === '0' || u?.signup_status === '2');
                return (
                  <tr key={u?.userId || u?.email || idx} className={styles.uTr}>
                    <td className={styles.uTd}>
                      <div className={styles.uNameCell}>
                        <span className={styles.uAvatar}>
                          {u?.dpURL || u?.image ? (
                            <img src={u.dpURL || u.image} alt={name} />
                          ) : name.charAt(0).toUpperCase()}
                        </span>
                        <span className={styles.uNameText}>
                          <span className={styles.uName}>{name}</span>
                          <span className={styles.uEmail}>{u?.email}</span>
                        </span>
                      </div>
                    </td>
                    <td className={styles.uTd}>
                      <span className={`${styles.planChip} ${expired ? styles.planChipExpired : ''}`}>
                        {planLabel(u)}
                      </span>
                      {u?.promo?.code && (
                        <Tooltip
                          arrow
                          title={(
                            <span style={{ display: 'block', lineHeight: 1.6, fontSize: 12 }}>
                              <b>{u.promo.code}</b>
                              {' — '}
                              {promoOffLabel(u.promo)}
                              <br />
                              {`Saved ${money(u.promo.discount_amount)}`}
                              <br />
                              {`Plan ${money(u.promo.plan_amount)} → Paid ${money(u.promo.final_amount)}`}
                            </span>
                          )}
                        >
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              marginLeft: 8,
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: 'rgba(90,165,239,0.12)',
                              color: '#2b6cb0',
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: 'default',
                              verticalAlign: 'middle',
                            }}
                          >
                            {`🏷 ${u.promo.code}`}
                          </span>
                        </Tooltip>
                      )}
                    </td>
                    <td className={`${styles.uTd} ${styles.uMuted}`}>{lastAccessedLabel(u)}</td>
                    <td className={`${styles.uTd} ${styles.uTdRight}`}>
                      <div className={styles.uRowActions}>
                        {canViewViewers && canAccess && Number(u?.viewers_count) > 0 && (
                          <button
                            type="button"
                            className={styles.uRowActBtn}
                            onClick={() => openViewers(u)}
                            title="View this account's viewers"
                          >
                            {`View viewers (${u.viewers_count})`}
                          </button>
                        )}
                        {needsFinish && (
                          <button
                            type="button"
                            className={styles.accessBtn}
                            onClick={() => finishSetup(u)}
                          >
                            Finish Setup
                          </button>
                        )}
                        {canInvite && (
                          <>
                            <button
                              type="button"
                              className={styles.uRowActBtn}
                              onClick={() => sendInvite(u)}
                              disabled={invitingId === u.userId}
                            >
                              {/* eslint-disable-next-line no-nested-ternary */}
                              {invitingId === u.userId ? 'Sending…' : (u?.signup_status === '2' ? 'Resend Email' : 'Send Email')}
                            </button>
                            <button
                              type="button"
                              className={styles.uRowActBtn}
                              onClick={() => copySignupLink(u)}
                              disabled={copyingUserId === u.userId}
                            >
                              {copyingUserId === u.userId ? 'Copying…' : 'Copy link'}
                            </button>
                          </>
                        )}
                        {canAccess ? (
                          <button type="button" className={styles.accessBtn} onClick={() => onAccessClick(u)}>
                            Access
                          </button>
                        ) : (!canInvite && !needsFinish && <span className={styles.uMuted}>—</span>)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- Access confirm ---- */}
      {accessTarget && (
        <div className={styles.modalOverlay} onClick={() => { if (!accessing) setAccessTarget(null); }}>
          <div className={styles.confirmCard} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalBody}>
              <h3 className={styles.confirmTitle}>{`Access ${rowName(accessTarget)}'s account?`}</h3>
              <p className={styles.confirmText}>
                You&apos;ll switch into this account and act on their behalf until you exit.
              </p>
              <div className={styles.confirmActions}>
                <button type="button" className={styles.coSecondary} onClick={() => setAccessTarget(null)} disabled={accessing}>
                  Cancel
                </button>
                <button type="button" className={styles.gateModalBtn} onClick={confirmAccess} disabled={accessing}>
                  {accessing ? 'Accessing…' : 'Access account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- View viewers: active + pending list ---- */}
      {viewersTarget && (
        <div className={styles.modalOverlay} onClick={() => setViewersTarget(null)}>
          <div
            className={styles.confirmCard}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 520, width: '92%' }}
          >
            <div className={styles.modalBody}>
              <h3 className={styles.confirmTitle} style={{ marginBottom: 4 }}>
                {`${rowName(viewersTarget)}'s viewers`}
              </h3>
              <p className={styles.confirmText} style={{ marginBottom: 16 }}>
                {viewersLoading
                  ? 'Loading viewers…'
                  : `${viewersActive.length} active · ${viewersPending.length} pending`}
              </p>
              {!viewersLoading && (
                <div style={{
                  maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8,
                }}
                >
                  {viewersActive.length === 0 && viewersPending.length === 0 ? (
                    <p className={styles.uMuted} style={{ textAlign: 'center', padding: '16px 0' }}>
                      No viewers yet.
                    </p>
                  ) : (
                    <>
                      {viewersActive.map((v, i) => renderViewerRow(v, 'active', i))}
                      {viewersPending.map((v, i) => renderViewerRow(v, 'pending', i))}
                    </>
                  )}
                </div>
              )}
              <div className={styles.confirmActions} style={{ marginTop: 16 }}>
                <button type="button" className={styles.exportBtn} onClick={() => setViewersTarget(null)}>
                  Close
                </button>
                <button
                  type="button"
                  className={styles.exportBtn}
                  onClick={exportViewersToCSV}
                  disabled={viewersLoading || (viewersActive.length + viewersPending.length === 0)}
                  title="Export these viewers as CSV"
                >
                  <span className={styles.exportIcon} aria-hidden="true">↓</span>
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Finish Setup: plan picker → payment (popup, like creation) ---- */}
      <FinishSetupModal
        open={!!finishUser}
        user={finishUser}
        onClose={() => setFinishUser(null)}
        onPaid={() => {
          const u = finishUser;
          setFinishUser(null);
          // Match the creation flow's "Access the account" outcome.
          switchingFunction?.(u);
        }}
      />
    </div>
  );
};

export default UsersPanel;
